export interface ServiceData {
  slug: string;
  category: "print" | "signage" | "digital";
  title: string;
  headline: string;
  desc: string;
  longDesc: string;
  features: string[];
  faqs: { q: string; a: string; link?: { href: string; label: string } }[];
  relatedSlugs: string[];
  /** Leaf "type" pages that live under this category page. Rendered as a
   *  "Shop by Type" block so the parent passes contextual internal-link equity
   *  down to its high-intent variants (SEO silo: home → category → service).
   *  Leaf pages link back up via relatedSlugs. */
  variantSlugs?: string[];
  // When set, the service card on /services links here (new tab) instead
  // of the internal /services/[slug] page, and /services/[slug] redirects
  // to this URL. Used for offerings that live on a separate property
  // (e.g. digital.areaadvertising.com).
  externalUrl?: string;
}

export const categories = [
  { id: "print", name: "Print & Apparel" },
  { id: "signage", name: "Signage & Awards" },
  { id: "digital", name: "Digital" },
] as const;

const DIGITAL_SITE = "https://digital.areaadvertising.com";

export const services: ServiceData[] = [
  {
    slug: "screen-printing",
    category: "print",
    title: "Screen Printing",
    headline: "Custom Screen Printing in Ashland, KY",
    desc: "High-quality screen printed apparel for businesses, non-profits, schools, bands, and sports leagues, produced in-house with a consultative approach.",
    longDesc:
      "Area Advertising is a customer-service-focused shop specializing in high-quality screen printed apparel for businesses, non-profit organizations, schools, bands, and sports leagues. We take a consultative approach to the screen printing process and work side by side with you so the final print matches your vision. Custom screen printed t-shirts are a cost-effective way to publicize your business, charity, club, group, band, or event.",
    features: [
      "Consultative process: we work alongside you from art to delivery",
      "Quality-guaranteed prints",
      "Suited to teams, leagues, non-profits, schools, and events",
      "In-house production at our Ashland, KY facility",
      "Custom apparel and accessories",
    ],
    faqs: [
      {
        q: "What kinds of organizations do you print for?",
        a: "Businesses, non-profit organizations, schools, bands, and sports leagues. Anyone who wants quality custom apparel that publicizes their cause or brand.",
      },
      {
        q: "Do I need finished artwork to start?",
        a: "No. We take a consultative approach and work with you through the process. Whether you have finished art, a sketch, or just an idea, we'll help develop it into something print-ready.",
      },
      {
        q: "Do you guarantee the quality?",
        a: "Yes. We stand behind our prints, with consistent color and durable results on every run. Quality gets checked before anything leaves the shop.",
      },
      {
        q: "How much does custom screen printing cost?",
        a: "Every job is quoted individually because pricing depends on quantity, the number of ink colors, and the garments you choose. Larger runs cost less per shirt. Quotes are free, online or by phone, with no obligation.",
        link: { href: "/order", label: "Get a free quote" },
      },
      {
        q: "How fast can you turn an order around?",
        a: "Most orders ship the same week, with free local delivery in the Ashland area. If you are working toward a hard event date, tell us up front and we will confirm the timeline on your quote.",
      },
    ],
    relatedSlugs: ["dtf-transfers", "embroidery", "promotional-products"],
    variantSlugs: ["custom-t-shirts", "team-uniforms", "custom-hoodies"],
  },
  {
    slug: "dtf-transfers",
    category: "print",
    title: "DTF Transfers",
    headline: "Custom DTF Transfers & Printing in Ashland, KY",
    desc: "Full-color direct-to-film transfers that press onto cotton, polyester, blends, and dark garments — no screens, no minimums, no weeding.",
    longDesc:
      "DTF (direct-to-film) printing puts a full-color design onto a transfer film, which is then heat pressed onto the garment. Because there are no screens to burn and no color separations to set up, it is the practical choice for short runs, photographic artwork, and designs with more colors than a screen print would justify. It also presses onto fabrics that screen printing and vinyl struggle with — polyester, tri-blends, nylon, and dark garments all take a DTF transfer without a special ink or an underbase. One order can cover t-shirts, hoodies, hats, and bags with the same artwork, which is why teams and small businesses tend to end up here for anything under a couple of dozen pieces.",
    features: [
      "Full-color and photographic artwork, no color limit",
      "Works on cotton, polyester, blends, nylon, and dark garments",
      "No screen setup, so short runs stay affordable",
      "One design across shirts, hoodies, hats, and bags",
      "Soft, durable prints that survive normal washing",
      "Transfers pressed in-house at our Ashland, KY shop",
    ],
    faqs: [
      {
        q: "What is DTF printing?",
        a: "Direct-to-film. Your artwork is printed onto a transfer film and bonded to the garment with a heat press. It skips the screens and color separations a screen print needs, which is what makes small, full-color orders practical.",
      },
      {
        q: "When is DTF better than screen printing?",
        a: "Short runs and busy artwork. Screen printing gets cheaper the more shirts you order and the fewer colors you use, so it wins on big single-color runs. DTF has no setup per color, so a twelve-piece order with a photographic front costs what it costs whether it is one color or twenty.",
      },
      {
        q: "What fabrics can you press onto?",
        a: "Cotton, polyester, cotton-poly blends, tri-blends, and nylon, in light or dark colors. Performance and athletic fabrics that are awkward for screen printing take DTF well, which is why team orders often go this route.",
      },
      {
        q: "Is there a minimum order?",
        a: "No. Because there are no screens to set up, a single shirt is a real order. Larger runs still bring the per-piece price down, so tell us the quantity you are considering and we will show you where it lands.",
      },
      {
        q: "How long do DTF prints last?",
        a: "Through normal washing and wearing, the same as any quality print. Wash inside out in cold water and skip the dryer's hottest setting and a DTF print will outlast the garment it is on.",
      },
      {
        q: "How much do DTF transfers cost?",
        a: "Every job is quoted individually based on print size, quantity, and the garments you choose. Quotes are free, online or by phone, with no obligation.",
        link: { href: "/order", label: "Get a free quote" },
      },
    ],
    relatedSlugs: ["screen-printing", "embroidery"],
    variantSlugs: ["custom-t-shirts", "custom-hoodies", "team-uniforms"],
  },
  {
    slug: "custom-t-shirts",
    category: "print",
    title: "Custom T-Shirts",
    headline: "Custom T-Shirts in Ashland, KY",
    desc: "Custom printed t-shirts for businesses, schools, teams, events, and fundraisers — one shirt or a thousand.",
    longDesc:
      "Custom t-shirts are the thing this shop has made more of than anything else since 1975. Businesses order them for staff and giveaways, schools for spirit weeks and field trips, teams for warmups and tournaments, families for reunions and memorials, churches for camps, and half the fundraisers in the Tri-State run on them. Area Advertising prints them here in Ashland, on garments we stock through SanMar and S&S Activewear, so you are choosing from the same catalog the big online printers use — without shipping your order across the country and hoping the size run is right. How your shirts get printed depends on the order rather than on what we feel like running. Larger runs with a few colors go on the screen printing press, where the price drops the more you order. Small runs, photographic artwork, and designs with too many colors to separate go on DTF transfers, which have no screen setup and therefore no real minimum. You do not have to know which one you want — tell us the quantity, the artwork, and the shirts, and we will tell you which way is cheaper and why.",
    features: [
      "Screen printed or DTF, whichever suits your order",
      "No minimum — a single shirt is a real order",
      "Youth through extended adult sizing",
      "Cotton, blends, and performance fabrics",
      "Blanks from SanMar and S&S Activewear",
      "Front, back, sleeve, and pocket prints",
      "Design help from a logo, a sketch, or finished art",
      "Printed in our Ashland, KY shop",
    ],
    faqs: [
      {
        q: "Is there a minimum order for custom t-shirts?",
        a: "No. DTF transfers have no screen setup, so a single shirt is a normal order here. Larger runs move to screen printing and the per-shirt price drops as the quantity goes up.",
      },
      {
        q: "How much do custom t-shirts cost?",
        a: "Three things move the price: how many shirts, how many colors in the design, and which garment you pick. Twelve shirts and two hundred shirts are very different per-shirt numbers. Every order is quoted individually, free and with no obligation.",
        link: { href: "/order", label: "Get a free quote" },
      },
      {
        q: "Screen printing or DTF — which do I need?",
        a: "Bigger runs with a handful of colors are cheaper screen printed, because the setup is spread across more shirts. Small runs and full-color or photographic artwork are cheaper on DTF, which has no setup per color. You do not need to decide — tell us the quantity and send the artwork, and we will tell you which is cheaper for your job.",
      },
      {
        q: "What kind of artwork do you need?",
        a: "The bigger and higher-resolution the better. Vector files (AI, EPS, PDF) are ideal, but a large PNG or a good photo of an existing shirt is usually enough to start. If all you have is an idea, we will develop it with you — that is how a lot of these start.",
      },
      {
        q: "Can I bring my own shirts?",
        a: "Ask us before you buy them. It is sometimes workable, but garments we have not sourced carry real risk — unknown fabric behaves differently under heat, and if one is ruined during printing it cannot be replaced from our stock. Nine times out of ten it is cheaper and safer to order the blanks through us.",
      },
      {
        q: "Do you have youth and extended sizes?",
        a: "Yes. Most styles run youth through extended adult sizes, though the exact range depends on the garment. Larger sizes sometimes carry an upcharge from the manufacturer, which we will show on the quote rather than surprise you with.",
      },
      {
        q: "Can you print on the back and sleeves too?",
        a: "Yes — front, back, sleeves, and pocket prints. Each additional print location adds to the cost, so tell us everywhere you want ink when you ask for a quote and the number you get will be the real one.",
      },
      {
        q: "How fast can you turn t-shirts around?",
        a: "Most orders ship the same week, with free local delivery in the Ashland area. If you are working toward a hard date — a tournament, a reunion, an event — say so up front and we will confirm the timeline on your quote before you commit.",
      },
      {
        q: "Do you do shirts for fundraisers and teams?",
        a: "Constantly. Schools, leagues, churches, and booster clubs are a large share of what goes through the shop. If you are running sizes across a roster or collecting orders from a group, we can help you structure it so you are not guessing at the size run.",
      },
    ],
    relatedSlugs: ["screen-printing", "dtf-transfers", "embroidery"],
  },
  {
    slug: "embroidery",
    category: "print",
    title: "Embroidery",
    headline: "Custom Embroidery Services in Ashland, KY",
    desc: "The most popular of all apparel embellishment techniques: durable, professional, and built to last.",
    longDesc:
      "Embroidery is the most popular of all apparel embellishment techniques. It creates a professional, classy look that's durable and won't wear off, perfect for staff uniforms and client or employee gifts. Our skilled and knowledgeable staff has over a decade of experience helping clients select the right materials and ensure quality stitching across a wide variety of styles and colors.",
    features: [
      "Over a decade of embroidery experience",
      "Custom embroidered shirts, jackets, and hats",
      "Durable, won't wear off",
      "Wide variety of styles and colors",
      "Ideal for staff uniforms and corporate gifts",
      "Personalized consultation on materials and design",
    ],
    faqs: [
      {
        q: "Why choose embroidery over screen printing?",
        a: "Embroidery creates a more upscale, classy appearance and is the most durable apparel embellishment option. It won't crack, peel, or wear off the way printed designs eventually can.",
      },
      {
        q: "What kinds of garments can you embroider?",
        a: "Shirts, jackets, hats, and most woven fabrics. We carry a wide variety of styles and colors and our staff will help you pick the right item for your application.",
      },
      {
        q: "What are embroidered items used for?",
        a: "Most commonly: uniforms, team apparel, gifts, and recognition pieces. Embroidery gives your name or logo a polished, sophisticated look, whether it's for a business, school, team, or church.",
      },
      {
        q: "How much does custom embroidery cost?",
        a: "Every job is quoted individually. Embroidery pricing depends on quantity, the size and stitch count of your design, and the garments you choose. Your logo only needs to be digitized once, and reorders use the same file. Quotes are free with no obligation.",
        link: { href: "/order", label: "Get a free quote" },
      },
    ],
    relatedSlugs: ["screen-printing", "promotional-products"],
    variantSlugs: ["embroidered-hats", "logo-digitizing", "staff-uniforms"],
  },
  {
    slug: "promotional-products",
    category: "print",
    title: "Promotional Products",
    headline: "Branded Promotional Products in Ashland, KY",
    desc: "Your logo can go on almost anything: pens, koozies, coolers, hats, bracelets, and over a million other items.",
    longDesc:
      "Your logo can go on almost anything, to be given out almost anywhere. Through our distributor network you can choose from over one million different items, from pens and pencils to koozies, coolers, hats, and silicon bracelets. Our experts will recommend just the right item to make a lasting impression and extend your message beyond the length of any event.",
    features: [
      "Over 1,000,000 products to choose from",
      "Expert recommendations on the right product for your goal",
      "Pens, drinkware, hats, bags, koozies, bracelets and more",
      "Your logo applied across the catalog",
      "Event-ready packaging and delivery",
    ],
    faqs: [
      {
        q: "Where can I view your promotional products?",
        a: "You can browse our full online catalog of over a million promotional products anytime. Find what you like, then send it our way and we'll quote it.",
        link: { href: "https://areaadvertising.espwebsite.com/", label: "View our promo catalog" },
      },
      {
        q: "How many products do you actually have access to?",
        a: "Over one million different items through our distributor partnerships, including PCNA and other top suppliers.",
      },
      {
        q: "What kinds of promotional products are most popular?",
        a: "Pens and pencils, koozies, coolers, hats, and silicon bracelets are common, but our experts will recommend the right item for your specific event, audience, and budget.",
      },
      {
        q: "Can you put our logo on any of the products?",
        a: "Almost anything in the catalog can carry your logo or design: pens, drinkware, hats, bags, and more. If you have a specific item in mind, tell us and we'll let you know what's possible.",
      },
    ],
    relatedSlugs: ["screen-printing", "embroidery"],
  },
  {
    slug: "laser-engraving",
    category: "print",
    title: "Laser Engraving",
    headline: "Precision Laser Engraving in Ashland, KY",
    desc: "Laser engraving, etching, and cutting on name plates, signs, plastic labels, wood, stainless steel, and a wide range of other materials.",
    longDesc:
      "Area Advertising offers laser engraving, etching, and cutting with laser precision on a wide range of materials, including name plates, signs, plastic labels, wood, and stainless steel products. We can engrave traditional materials and some items you would never imagine. Our knowledgeable staff can assist you in finding the most cost-effective engraving solution for your project, plus we offer custom design services for unique gifts, awards, and personalization.",
    features: [
      "Engraving, etching, and laser cutting",
      "Name plates and signs",
      "Plastic labels",
      "Wood and stainless steel",
      "Custom design services for one-off gifts and awards",
      "Cost-effective solutions across project sizes",
    ],
    faqs: [
      {
        q: "What materials can you engrave?",
        a: "Name plates, signs, plastic labels, wood, and stainless steel are the most common, but we can engrave a wide range of materials and items you might not expect. Bring us a sample and we'll see what works.",
      },
      {
        q: "Can you help with the design?",
        a: "Yes. We offer custom design services for unique gifts, awards, and personalization. We'll capture your message, logo, or graphic and laser it onto the material.",
      },
      {
        q: "How do I pick the right engraving option for my project?",
        a: "Our staff will walk you through the options and help you land on the most cost-effective engraving solution for your specific project and material.",
      },
    ],
    relatedSlugs: ["trophies-awards", "bronze-plaques"],
  },
  {
    slug: "signs-banners",
    category: "signage",
    title: "Signs & Banners",
    headline: "Custom Signs & Banners in Ashland, KY",
    desc: "Full-color, digitally printed banners and signs for on-site marketing, trade shows, sporting events, and special events.",
    longDesc:
      "Draped on building walls, suspended over streets and hung from poles, custom banners and signs command attention for sheer size and position. Our full-color banners are great for on-site marketing, trade shows, sporting events, and special events. Banners and signs are an effective marketing tool widely used by clubs, sport teams, and businesses. Stand out and make a lasting impression with a well designed, digitally printed banner or sign.",
    features: [
      "Full-color digital printing",
      "Indoor and outdoor banners",
      "Sized for streets, poles, and building walls",
      "Built for trade shows, sporting events, and special events",
      "Designed for clubs, sports teams, and businesses",
      "Custom-tailored design assistance",
    ],
    faqs: [
      {
        q: "What are banners and signs good for?",
        a: "On-site marketing, trade shows, sporting events, and special events. They work especially well for clubs, sports teams, and businesses that want to stand out and make a lasting impression.",
      },
      {
        q: "Where can you hang them?",
        a: "Draped on building walls, suspended over streets, hung from poles, wherever they'll command attention by size and position.",
      },
      {
        q: "Can you design from scratch?",
        a: "Yes. Our team can develop a banner around your logo or brief, or work from existing artwork you provide.",
      },
      {
        q: "How much do custom signs and banners cost?",
        a: "Every job is quoted individually based on size, material, and quantity. Quotes are free, online or by phone, with no obligation.",
        link: { href: "/order", label: "Get a free quote" },
      },
    ],
    relatedSlugs: ["vehicle-decals", "trophies-awards"],
    variantSlugs: ["yard-signs", "business-signs", "custom-banners"],
  },
  {
    slug: "vehicle-decals",
    category: "signage",
    title: "Vehicle Marketing",
    headline: "Vehicle Marketing & Graphics in Ashland, KY",
    desc: "Turn your vehicles into rolling billboards, with up to 16 million impressions per year per vehicle (Traffic Audit Bureau).",
    longDesc:
      "According to the Traffic Audit Bureau, one vehicle graphic can generate up to 16 million impressions in just one year. That makes vehicle graphics one of the easiest and most cost-effective ways to advertise any business: a one-time cost with continuous exposure as your vehicles travel their normal routes. Area Advertising is a full-service vehicle graphics company offering high-quality solutions from design through installation.",
    features: [
      "Up to 16M impressions/year per vehicle (Traffic Audit Bureau)",
      "Full-service: design + production + installation",
      "One-time cost, continuous exposure",
      "Fleet branding across multiple vehicles",
      "High-quality finishes built for the road",
    ],
    faqs: [
      {
        q: "How effective is vehicle advertising?",
        a: "Per the Traffic Audit Bureau, one vehicle graphic can generate up to 16 million impressions in a single year. It's one of the highest-ROI advertising channels available.",
      },
      {
        q: "Do you handle the install or just produce the graphics?",
        a: "Full service. We design, produce, and install, start to finish, so you're not coordinating multiple vendors.",
      },
      {
        q: "Can you do an entire fleet with consistent branding?",
        a: "Yes. We provide high-quality vehicle solutions from beginning to end, including fleets with multiple vehicle types.",
      },
    ],
    relatedSlugs: ["signs-banners", "trophies-awards"],
  },
  {
    slug: "trophies-awards",
    category: "signage",
    title: "Trophies & Awards",
    headline: "Custom Trophies & Awards in Ashland, KY",
    desc: "Trophies, plaques, medals, and engraved awards for leagues, tournaments, schools, and company recognition.",
    longDesc:
      "Area Advertising supplies and engraves custom trophies, plaques, medals, and awards for youth leagues, school programs, tournaments, banquets, and workplace recognition across Ashland and the Tri-State. That covers the whole range: traditional column trophies and cups, acrylic and crystal awards for banquets, wall and perpetual plaques, dog tags and medals for a whole roster, and engraved gifts for retirements and service anniversaries. Engraving is done here, which is the part that matters when a tournament date will not move — and it is also why we can handle a late name change or an extra award without restarting the order. Two things worth thinking about before you order: how many people you are recognising, since participant awards and top honours are usually different pieces at very different prices, and whether this is an annual program. If it is, a perpetual plaque with add-on plates costs far less over five years than five separate awards, and we keep your artwork and layout on file so next season is a phone call rather than a fresh setup.",
    features: [
      "Column trophies, cups, and figures",
      "Acrylic and crystal awards for banquets",
      "Wall plaques and perpetual plaques with add-on plates",
      "Medals, dog tags, and full-roster participant awards",
      "Retirement, service, and employee recognition gifts",
      "Laser engraving done in-house",
      "Artwork and layouts kept on file for annual programs",
      "Sourced through JDS, Marco Awards Group, and Award Catalog",
    ],
    faqs: [
      {
        q: "Can you do awards for a whole league or roster?",
        a: "Yes, and it is a lot of what we do. Participant medals or dog tags for every player alongside a handful of larger awards for top honours is the usual shape, and it keeps the cost per child sensible while the winners still get something that feels like a win.",
      },
      {
        q: "What kinds of trophies and awards do you carry?",
        a: "Column trophies and cups, acrylic and crystal awards, wall and perpetual plaques, medals and dog tags, and engraved recognition gifts. If you have seen something you like, bring a photo — we can usually source it or something close.",
      },
      {
        q: "Is engraving included?",
        a: "Engraving is done in-house, so it is quoted as part of the job rather than farmed out. That is also what lets us handle a late name change without the whole order slipping.",
      },
      {
        q: "Can you turn awards around for a tournament date?",
        a: "Tell us the date up front and we will confirm it on your quote before you commit. Engraving in-house is what makes a tight date workable, but a hard deadline needs to be said out loud at the start, not discovered at the end.",
      },
      {
        q: "What is a perpetual plaque, and do I want one?",
        a: "A single wall plaque with room for small engraved plates added each year. If you run an annual award, it costs far less over five years than five separate pieces, and it looks better on the wall than a shelf of mismatched trophies.",
      },
      {
        q: "Do you keep our artwork for next year?",
        a: "Yes. We keep artwork and layouts on file, so a repeat order for next season is a phone call and a list of names rather than starting over.",
      },
      {
        q: "How much do custom trophies and awards cost?",
        a: "It depends on the pieces and the quantity — participant medals and a crystal banquet award are very different numbers. Every order is quoted individually, free, with no obligation.",
        link: { href: "/order", label: "Get a free quote" },
      },
    ],
    relatedSlugs: ["laser-engraving", "bronze-plaques"],
  },
  {
    slug: "bronze-plaques",
    category: "signage",
    title: "Bronze Plaques",
    headline: "Custom Bronze Plaques in Ashland, KY",
    desc: "Quality brass, bronze, and aluminum plaques, poured, painted, and finished by hand. No imitation metals.",
    longDesc:
      "Each bronze plaque is custom made to your specifications. We use only quality brass, bronze, and aluminum, no imitation metals, and every element is poured, painted, and finished by hand. The result is a quality product that will endure the test of time. Our plaques look great indoors and can withstand the outdoor elements, making them ideal for memorials, donor recognition, bronze signs, address plaques, and business logos. We provide design assistance, proofs, and cost estimates before final approval.",
    features: [
      "Real brass, bronze, and aluminum, no imitation metals",
      "Poured, painted, and finished by hand",
      "Memorials and donor recognition plaques",
      "Bronze signs and address plaques",
      "Business logo plaques",
      "Indoor and outdoor durability",
      "Design assistance, proofs, and estimates included",
    ],
    faqs: [
      {
        q: "What metals do you use?",
        a: "Only quality brass, bronze, and aluminum, no imitation metals. Every element is poured, painted, and finished by hand.",
      },
      {
        q: "What are bronze plaques typically used for?",
        a: "Memorials, donor plaques, bronze signs, address plaques, and business logos.",
      },
      {
        q: "Can they go outside?",
        a: "Yes. Our bronze plaques look great indoors and can withstand the outdoor elements, built as a quality product that will endure the test of time.",
      },
    ],
    relatedSlugs: ["trophies-awards", "laser-engraving"],
  },
  // ─── SEO leaf pages — Core 30 build queue (see docs/seo-core-30.md) ────────
  // Targeted pages for high-intent searches that were previously folded into
  // broader service pages and diluted.
  {
    slug: "team-uniforms",
    category: "print",
    title: "Team Uniforms",
    headline: "Custom Team Uniforms in Ashland, KY",
    desc: "Matching uniforms for sports leagues, schools, and clubs, screen printed or embroidered in-house.",
    longDesc:
      "Area Advertising outfits sports leagues, school teams, and clubs across the Tri-State with custom uniforms. Whether you need screen printed jerseys for a youth baseball league or embroidered polos for a coaching staff, we work with you from names and numbers to colors and sizing. We have supplied local teams since 1975 and take a consultative approach so every player looks like part of the same squad.",
    features: [
      "Screen printed and embroidered uniform options",
      "Player names and numbers",
      "Youth and adult sizing",
      "Matching coach and staff apparel",
      "Bulk pricing for full rosters",
      "Locally produced in Ashland, KY",
    ],
    faqs: [
      {
        q: "Can you do names and numbers on each jersey?",
        a: "Yes. Individual player names and numbers are part of the uniform order, along with team logos and sponsor names.",
      },
      {
        q: "Do you outfit whole leagues?",
        a: "We do. From a single team to a full league, we produce matching uniforms and help coordinate colors and sizing across rosters.",
      },
      {
        q: "Screen printing or embroidery for uniforms?",
        a: "Both work. Screen printing suits bold jersey designs and larger runs; embroidery suits polos, jackets, and coaching staff apparel. We will recommend the right fit for your sport and budget.",
      },
    ],
    relatedSlugs: ["screen-printing", "embroidery", "custom-hoodies"],
  },
  {
    slug: "embroidered-hats",
    category: "print",
    title: "Embroidered Hats",
    headline: "Custom Embroidered Hats & Caps in Ashland, KY",
    desc: "Embroidered caps, beanies, and hats with your logo, stitched in-house.",
    longDesc:
      "A custom embroidered hat is a walking advertisement that lasts for years. Area Advertising stitches logos onto ball caps, trucker hats, beanies, and visors for businesses, teams, and events. Our embroidery staff has over a decade of experience and will help you pick the right cap style and thread colors so your logo looks sharp on the front panel or the curve of the bill.",
    features: [
      "Embroidered ball caps and trucker hats",
      "Beanies and winter hats",
      "Logo digitizing for clean stitching",
      "Wide range of cap styles and colors",
      "Staff, team, and event hats",
      "Locally embroidered in Ashland, KY",
    ],
    faqs: [
      {
        q: "Why embroidery instead of a printed hat?",
        a: "Embroidery holds up far better on hats. A stitched logo will not crack or peel, and it gives caps a more finished, professional look.",
      },
      {
        q: "Do you digitize the logo?",
        a: "Yes. We convert your logo into an embroidery file so it stitches cleanly, and that file is reusable for future hat or apparel orders.",
      },
      {
        q: "What hat styles can you embroider?",
        a: "Structured and unstructured ball caps, trucker hats, snapbacks, beanies, and visors. We will help you choose based on your logo and audience.",
      },
    ],
    relatedSlugs: ["embroidery", "staff-uniforms", "screen-printing"],
  },
  {
    slug: "logo-digitizing",
    category: "print",
    title: "Logo Digitizing",
    headline: "Logo Digitizing for Embroidery in Ashland, KY",
    desc: "Convert your logo into a clean, production-ready embroidery stitch file.",
    longDesc:
      "Before a logo can be embroidered it has to be digitized, converted into a stitch file that tells the machine exactly how to sew it. Area Advertising digitizes logos in-house so your embroidery comes out crisp and consistent. Once your logo is digitized, the file is reusable for every future hat, polo, jacket, or uniform order.",
    features: [
      "In-house logo digitizing",
      "Clean, production-ready stitch files",
      "Reusable across future orders",
      "Handles detailed and multi-color logos",
      "Paired with our embroidery service",
      "Over a decade of embroidery experience",
    ],
    faqs: [
      {
        q: "What is logo digitizing?",
        a: "It is the process of converting your logo artwork into a stitch file an embroidery machine can read. Good digitizing is the difference between a clean logo and a messy one.",
      },
      {
        q: "Do I need my logo digitized every time?",
        a: "No. Once your logo is digitized we keep the file and reuse it for any future embroidery order: hats, polos, jackets, and more.",
      },
      {
        q: "Can you digitize a detailed logo?",
        a: "Most logos digitize well. Very fine detail or small text can be a challenge at small sizes, and our staff will tell you straight what will and will not stitch cleanly.",
      },
    ],
    relatedSlugs: ["embroidery", "embroidered-hats", "screen-printing"],
  },
  {
    slug: "staff-uniforms",
    category: "print",
    title: "Staff Uniforms",
    headline: "Custom Staff Uniforms in Ashland, KY",
    desc: "Branded polos, shirts, and workwear that make your team look professional.",
    longDesc:
      "Matching staff uniforms make a business look organized and trustworthy, and they turn every employee into a brand ambassador. Area Advertising produces custom staff uniforms for restaurants, contractors, retail shops, medical offices, and service businesses across the Tri-State. Embroidered polos, screen printed tees, button-downs, and workwear, sized for your whole team.",
    features: [
      "Embroidered polos and button-downs",
      "Screen printed staff shirts",
      "Workwear and hi-vis options",
      "Consistent branding across your team",
      "Sizing for the whole staff",
      "Locally produced in Ashland, KY",
    ],
    faqs: [
      {
        q: "What kinds of businesses do you outfit?",
        a: "Restaurants, contractors, retail shops, medical and dental offices, salons, and service businesses of all kinds. If your team should look uniform, we can help.",
      },
      {
        q: "Embroidery or screen printing for staff uniforms?",
        a: "Embroidery suits polos and button-downs and lasts for years. Screen printing suits tees and bolder designs. We will recommend the right mix for your team and setting.",
      },
      {
        q: "Can we reorder as we hire?",
        a: "Yes. Once we have your logo and uniform specs on file, adding shirts for new hires is straightforward.",
      },
    ],
    relatedSlugs: ["embroidery", "screen-printing", "embroidered-hats"],
  },
  {
    slug: "custom-hoodies",
    category: "print",
    title: "Custom Hoodies",
    headline: "Custom Hoodies & Sweatshirts in Ashland, KY",
    desc: "Screen printed and embroidered hoodies for teams, schools, and businesses.",
    longDesc:
      "Custom hoodies and sweatshirts are a year-round favorite for schools, teams, clubs, and businesses. Area Advertising screen prints and embroiders hoodies, crewnecks, and quarter-zips for spirit wear, staff apparel, fundraisers, and events. Pick your blanks and colors and we will help develop a design that people actually want to wear.",
    features: [
      "Screen printed and embroidered hoodies",
      "Crewnecks and quarter-zips",
      "School and team spirit wear",
      "Fundraiser and event apparel",
      "Youth and adult sizing",
      "Locally produced in Ashland, KY",
    ],
    faqs: [
      {
        q: "Do you print on hoodies and crewnecks?",
        a: "Yes. We screen print and embroider pullover hoodies, zip-ups, crewnecks, and quarter-zips.",
      },
      {
        q: "Are hoodies good for fundraisers?",
        a: "They are popular fundraiser items because people genuinely wear them. We can help schools, teams, and groups put together a design and run sizes.",
      },
      {
        q: "Can you match our school or team colors?",
        a: "Yes. We carry a wide range of blank colors and will help match your design to your school or team palette.",
      },
    ],
    relatedSlugs: ["screen-printing", "team-uniforms", "embroidery"],
  },
  {
    slug: "yard-signs",
    category: "signage",
    title: "Yard Signs",
    headline: "Custom Yard Signs in Ashland, KY",
    desc: "Coroplast yard signs with stakes for real estate, campaigns, events, and local businesses.",
    longDesc:
      "Yard signs are the cheapest way to put a message in front of local traffic, which is why they show up everywhere from campaign season to a church chili supper. Area Advertising prints custom coroplast yard signs for real estate listings and open houses, election season, garage sales, graduations, sports sign-ups, and small business promotions — full-color, weather-ready, and printed in our Ashland shop with H-stakes so they are ready to plant. The thing worth deciding up front is single- or double-sided. A sign in a front yard that traffic passes in both directions should be printed both sides; one against a building or facing a single approach does not need to be, and printing one side keeps the cost down. Design matters more on a yard sign than almost anything else we print, because a driver has about two seconds: a few large words, strong contrast, and a phone number big enough to read at speed will beat a sign carrying three times the information every time.",
    features: [
      "Full-color corrugated plastic (coroplast) yard signs",
      "Single- or double-sided printing",
      "H-stakes that slide straight into the flutes",
      "Real estate, open house, and rider signs",
      "Campaign and election signs",
      "Event, directional, and parking signs",
      "One sign or a bulk run for a whole office or campaign",
    ],
    faqs: [
      {
        q: "What are yard signs printed on?",
        a: "Corrugated plastic — coroplast — which is light, cheap, and holds up outdoors through rain and sun. For signage that needs to stand for years rather than a season, ask us about rigid substrates instead.",
      },
      {
        q: "Do you provide the stakes?",
        a: "Yes. H-stakes slide into the flutes of the coroplast, so your signs are ready to plant when you collect them. Tell us how many you need staked and we will include them.",
      },
      {
        q: "Should my yard signs be single- or double-sided?",
        a: "Double-sided if traffic passes in both directions, which is the usual case for a front yard or a corner. Single-sided is fine — and cheaper — when the sign faces one approach or sits against a wall.",
      },
      {
        q: "Can you print just one yard sign?",
        a: "Yes. One-offs are normal, whether it is a single listing or one event. Bulk runs for real estate offices, campaigns, and tournaments cost noticeably less per sign.",
      },
      {
        q: "What should go on a yard sign?",
        a: "Less than you think. A driver reads a yard sign in about two seconds, so a few large words, high contrast, and one clear way to respond will outperform a sign that tries to say everything. We will tell you honestly if a design is carrying too much.",
      },
      {
        q: "How much do custom yard signs cost?",
        a: "Quantity is the biggest factor, then whether they are single- or double-sided. Every order is quoted individually, free, with no obligation.",
        link: { href: "/order", label: "Get a free quote" },
      },
    ],
    relatedSlugs: ["signs-banners", "business-signs", "custom-banners"],
  },
  {
    slug: "business-signs",
    category: "signage",
    title: "Business Signs",
    headline: "Custom Business Signs in Ashland, KY",
    desc: "Storefront, lobby, and exterior signs that make your business easy to find and easy to trust.",
    longDesc:
      "Your sign is doing work before anyone walks in. It tells people you exist, that you are open, and — fairly or not — how seriously you take your own business. Area Advertising designs and produces business signage for storefronts, offices, lobbies, and entrances across Ashland and the Tri-State: storefront and window signage, dimensional lettering, reception and lobby signs, directional and wayfinding signs, hours and door graphics. What we spend the most time on is the part people skip: legibility at the distance and speed your customers are actually approaching from. A sign read from a sidewalk and a sign read from a four-lane road are different signs, even with the same logo on them. Bring us the space — a photo of the storefront and rough measurements is plenty to start — and we will work back from where it needs to be seen to what it should be made of and how big the lettering has to be.",
    features: [
      "Storefront and window signage",
      "Lobby, reception, and office signs",
      "Dimensional and cut lettering",
      "Directional and wayfinding signs",
      "Door graphics, hours, and window lettering",
      "Indoor and outdoor materials matched to the location",
      "Design from a photo and measurements, or from your existing artwork",
      "Produced locally in Ashland, KY",
    ],
    faqs: [
      {
        q: "What kinds of business signs do you make?",
        a: "Storefront and window signage, lobby and reception signs, dimensional lettering, directional and wayfinding signs, and door graphics. Tell us what the sign has to do and where it is going, and we will recommend an approach rather than sell you a product.",
      },
      {
        q: "Do you design the sign or only produce it?",
        a: "Both. Bring finished artwork and we will produce it. Bring a logo, a photo of the space, and a rough idea, and our team will develop the design with you.",
      },
      {
        q: "What do you need from me to quote a storefront sign?",
        a: "A photo of the space and rough measurements gets us most of the way. It also helps to know how far back people see it from — across a parking lot, from a passing road, or from the sidewalk — because that drives the size and the materials more than anything else.",
      },
      {
        q: "Do you help with installation?",
        a: "Talk to us about the specific sign and location. Installation depends heavily on the type of sign, the building, and what the property owner or municipality requires, so it is worth raising early rather than after the sign is built.",
      },
      {
        q: "Do I need a permit for a business sign?",
        a: "Sometimes, depending on your municipality, the type of sign, and whether it is illuminated or attached to the building. Check with your local office before committing to a size or placement — it is a much cheaper conversation to have first.",
      },
      {
        q: "How much does a business sign cost?",
        a: "It varies more than anything else we make, because size, material, and mounting all move the number. Every sign is quoted individually, free and with no obligation.",
        link: { href: "/order", label: "Get a free quote" },
      },
    ],
    relatedSlugs: ["signs-banners", "yard-signs", "vehicle-decals"],
  },
  {
    slug: "custom-banners",
    category: "signage",
    title: "Custom Banners",
    headline: "Custom Banners in Ashland, KY",
    desc: "Full-color vinyl banners for events, sales, sports, and grand openings, printed and finished in Ashland.",
    longDesc:
      "A banner earns attention by size and placement — draped down a building, strung over a street, zip-tied to an outfield fence. Area Advertising prints full-color custom banners for grand openings, seasonal sales, trade shows, tournaments, fundraisers, and church events, digitally printed on durable vinyl and finished so they are ready to hang the day you pick them up. Most orders are hemmed with grommets every couple of feet, which is what keeps a banner from tearing at the corners the first time the wind catches it. Where a banner is going changes how it should be built: a fence-line banner in an open field wants mesh so wind passes through instead of turning it into a sail, an indoor trade-show banner does not, and a banner going on a pole needs pockets sewn rather than grommets punched. Tell us where it is hanging and for how long, and we will spec it for that instead of handing you the same banner for every job.",
    features: [
      "Full-color digital printing, any artwork, no colour limit",
      "Hemmed edges and grommets, ready to hang",
      "Mesh option for fence lines and windy outdoor spots",
      "Pole pockets for hanging bars and street poles",
      "Indoor and outdoor, short-term event or full-season",
      "Single banners or bulk runs for multi-site businesses",
      "Design help from a logo, a sketch, or print-ready art",
    ],
    faqs: [
      {
        q: "What are custom banners made of?",
        a: "Most are printed on heavy scrim vinyl, hemmed around the edges and grommeted so they are ready to hang. For fence lines and exposed outdoor spots we will usually recommend mesh instead, which lets wind pass through rather than pulling against the ties.",
      },
      {
        q: "What size banner do I need?",
        a: "It depends on how far away people will read it from. A banner on a building read from across a parking lot needs much larger lettering than one behind a booth. Tell us where it is going and roughly how far back people will be standing, and we will size the banner and the text together.",
      },
      {
        q: "How long will an outdoor banner last?",
        a: "A properly hemmed and grommeted vinyl banner handles a season outdoors comfortably, and often much longer. What kills banners early is almost always how they are hung — too few tie points, or ties through the vinyl instead of the grommets.",
      },
      {
        q: "Can you design the banner for me?",
        a: "Yes. We can build one around your logo and message, work from a rough idea, or print artwork you already have. If you are supplying art, larger and higher resolution is better, since a banner enlarges every flaw in a small file.",
      },
      {
        q: "Can I order just one banner?",
        a: "Yes. One banner is a normal order here. Larger runs bring the per-banner price down, which matters if you are covering several locations or a whole tournament.",
      },
      {
        q: "How much does a custom banner cost?",
        a: "It comes down to size, material, and quantity, so every banner is quoted individually. Quotes are free, online or by phone, with no obligation.",
        link: { href: "/order", label: "Get a free quote" },
      },
    ],
    relatedSlugs: ["signs-banners", "business-signs", "yard-signs"],
  },
  // ─── Digital — handled by digital.areaadvertising.com ──────────────────────
  {
    slug: "web-design",
    category: "digital",
    title: "Web Design & Development",
    headline: "Custom Websites for Local Business",
    desc: "Fast, modern websites built to convert visitors into customers, designed and developed in-house.",
    longDesc:
      "Area Advertising Business Solutions builds custom websites for local and regional brands, engineered for speed, SEO, and conversion. From single-page sites to full e-commerce, hosting, and ongoing support, the team handles design, development, and launch end-to-end.",
    features: [
      "Custom design & development",
      "Mobile-first responsive layouts",
      "Performance & Core Web Vitals tuned",
      "On-page SEO baked in",
      "Hosting & maintenance available",
      "Integrated with your other tools",
    ],
    faqs: [],
    relatedSlugs: ["seo-optimization", "api-integrations", "digital-audit"],
    externalUrl: DIGITAL_SITE,
  },
  {
    slug: "seo-optimization",
    category: "digital",
    title: "SEO & Site Optimization",
    headline: "Rank Higher. Convert More.",
    desc: "Technical SEO, on-page optimization, and site speed work that moves the needle on search rankings.",
    longDesc:
      "Area Advertising Business Solutions handles the SEO and optimization work most local businesses skip: schema, sitemaps, page speed, Core Web Vitals, content structure, and on-page targeting. Measurable improvements you can see in Search Console.",
    features: [
      "Technical SEO audits",
      "On-page optimization",
      "Page speed & Core Web Vitals",
      "Schema markup & structured data",
      "Local SEO & Google Business Profile",
      "Reporting you can actually read",
    ],
    faqs: [],
    relatedSlugs: ["web-design", "digital-audit", "api-integrations"],
    externalUrl: DIGITAL_SITE,
  },
  {
    slug: "api-integrations",
    category: "digital",
    title: "API Integrations",
    headline: "Connect the Tools You Already Use",
    desc: "Custom API integrations that link your website to your CRM, fulfillment, accounting, and analytics tools.",
    longDesc:
      "Area Advertising Business Solutions wires up the systems your business runs on, connecting your website, CRM, fulfillment, payments, and analytics so data moves automatically instead of getting copy-pasted between tabs.",
    features: [
      "CRM & email platform integrations",
      "E-commerce & payments wiring",
      "Fulfillment & inventory sync",
      "Webhook & automation setup",
      "Custom internal tools",
      "Analytics & event tracking",
    ],
    faqs: [],
    relatedSlugs: ["web-design", "seo-optimization", "digital-audit"],
    externalUrl: DIGITAL_SITE,
  },
  {
    slug: "digital-audit",
    category: "digital",
    title: "Digital Audits",
    headline: "Find What's Actually Holding You Back",
    desc: "Comprehensive digital audits covering site performance, SEO, analytics, conversion gaps, and tech-stack health.",
    longDesc:
      "Area Advertising Business Solutions runs comprehensive digital audits across your site, SEO, analytics, conversion funnel, and integrations, then hands back a prioritized action plan with the highest-impact fixes first.",
    features: [
      "Site performance & accessibility",
      "SEO & content review",
      "Analytics & tracking validation",
      "Conversion funnel analysis",
      "Tech-stack health check",
      "Prioritized action plan",
    ],
    faqs: [],
    relatedSlugs: ["seo-optimization", "web-design", "api-integrations"],
    externalUrl: DIGITAL_SITE,
  },
  {
    slug: "marketing-agency",
    category: "digital",
    title: "Marketing Agency",
    headline: "Full-Service Marketing for Local Business",
    desc: "Strategy, branding, paid media, and content, handled by our digital arm, Area Advertising Business Solutions.",
    longDesc:
      "Marketing agency services, including brand strategy, paid media, content, and campaign management, are delivered by Area Advertising Business Solutions, our digital arm. Click through to see the full offering.",
    features: [
      "Brand strategy & positioning",
      "Paid media (Google, Meta)",
      "Content & creative production",
      "Campaign management & reporting",
    ],
    faqs: [],
    relatedSlugs: ["web-design", "seo-optimization", "digital-audit"],
    externalUrl: DIGITAL_SITE,
  },
  {
    slug: "business-development",
    category: "digital",
    title: "Business Development",
    headline: "Grow Your Local Business",
    desc: "Lead generation, sales enablement, and growth systems for small and mid-size businesses.",
    longDesc:
      "Business development services, including lead generation, sales process design, and growth systems, are delivered by Area Advertising Business Solutions, our digital arm. Click through to see the full offering.",
    features: [
      "Lead generation systems",
      "Sales process design",
      "CRM setup & automation",
      "Growth strategy & consulting",
    ],
    faqs: [],
    relatedSlugs: ["marketing-agency", "web-design", "api-integrations"],
    externalUrl: DIGITAL_SITE,
  },
];

export function getServiceBySlug(slug: string): ServiceData | undefined {
  return services.find((s) => s.slug === slug);
}

/**
 * Slugs that render a real page here.
 *
 * External-only services are EXCLUDED. /services/<slug> redirects them to
 * digital.areaadvertising.com, and a sitemap is a list of canonical, indexable
 * URLs — advertising six that answer with a redirect gets them crawled and
 * dropped as "Page with redirect", spends crawl budget a small site does not
 * have, and tells Google the site is about marketing and web design when the
 * pages worth ranking are the print and signage ones.
 *
 * generateStaticParams already filtered these out. The sitemap did not, which
 * is the half that search engines read.
 */
export function getServiceSlugs(): string[] {
  return services.filter((s) => !s.externalUrl).map((s) => s.slug);
}
